// Library of 5 professional trading strategies, each with
// all fields required by the detail page. Same structure
// so that future strategies can be added without touching the component.

export const ESTRATEGIAS = [
  {
    id: "smc-order-block",
    nombre: "SMC — Order Block Entry",
    tipo: "tendencia",
    mercados: ["BTC", "ETH", "Altcoins"],
    timeframes: ["1H", "4H", "D"],
    dificultad: "intermedia",
    winRate: "55-65%",
    ratioRR: "1:3 — 1:5",
    descripcion: "Institutional entry in Order Block zones after a Break of Structure (BOS). This is the core Smart Money Concepts strategy. The goal is for price to return to test the zone where institutions placed their orders, to enter alongside them.",
    condicionesEntrada: [
      "1. Identify the trend on the higher timeframe (D or 4H).",
      "2. Wait for a Break of Structure (BOS) in the direction of the trend.",
      "3. Identify the Order Block that caused the BOS (last opposite candle before the impulse).",
      "4. Wait for price to return to the Order Block range (retracement).",
      "5. Confirm with structure on lower timeframe (1H or 15m): bullish CHoCH at the OB.",
      "6. Enter in the upper half of the OB (discount zone within the OB).",
    ],
    stopLoss: "Below the Order Block low (with a small 0.5% margin).",
    takeProfit: "Next liquidity zone / previous high / Fibonacci 161.8% extension.",
    ejemploReal: "BTC on 4H shows bullish BOS breaking 44,000. The bullish OB is between 42,800-43,200 (last red candle before the impulse). Price returns to 43,000, on 1H a bullish CHoCH forms. Entry at 43,100, SL at 42,700, TP at 45,500. R:R ≈ 1:3.",
    erroresComunes: [
      "Entering the OB without waiting for confirmation on the lower timeframe.",
      "Not correctly identifying the higher timeframe trend.",
      "Setting the SL too tight within the OB — price can sweep it.",
      "Confusing the OB with a random consolidation zone.",
    ],
    consejosPro: [
      "OBs with FVG above are more powerful — double institutional confluence.",
      "The most valid OB is the one that caused the most recent BOS.",
      "Better to ignore OBs that have been tested more than 2 times.",
      "Combine with liquidity: enter when price first hunts stops and then returns to the OB.",
    ],
  },
  {
    id: "fvg-fill",
    nombre: "FVG Fill — Filling Inefficiencies",
    tipo: "reversion",
    mercados: ["BTC", "ETH", "All"],
    timeframes: ["15m", "1H", "4H"],
    dificultad: "basica",
    winRate: "60-70%",
    ratioRR: "1:2 — 1:3",
    descripcion: "Price tends to return to fill Fair Value Gaps (FVG) created by violent impulses. This strategy takes advantage of the market's tendency to seek price equilibrium. Simple and high probability when used in confluence with structure.",
    condicionesEntrada: [
      "1. Identify a strong impulse (bullish or bearish) with visible FVG.",
      "2. The FVG is the space between the high wick of candle 1 and low wick of candle 3.",
      "3. Wait for price to retrace towards the FVG.",
      "4. Better if the FVG coincides with an Order Block or support/resistance zone.",
      "5. Enter at 50% of the FVG (middle of the gap).",
      "6. Confirm with rejection candle or structure on lower TF.",
    ],
    stopLoss: "Below the complete FVG (if price crosses through it entirely, the FVG is no longer valid).",
    takeProfit: "The extreme from where the FVG formed — the original impulse.",
    ejemploReal: "ETH bullish impulse from 2,200 to 2,450. FVG between 2,280-2,320. Price retraces to 2,300, indecision doji candle. Entry at 2,300, SL at 2,270, TP at 2,420. R:R ≈ 1:4.",
    erroresComunes: [
      "Entering FVGs that have already been filled — a filled FVG loses its magnetism.",
      "Ignoring the macro trend — trading FVGs against trend has lower probability.",
      "Very small FVGs (<0.5%) have less statistical reliability.",
    ],
    consejosPro: [
      "FVGs in confluence with Order Blocks are the most powerful.",
      "An FVG filled halfway (50%) and rejected is a very strong signal.",
      "In an uptrend, trade only bullish FVGs. Don't fight the trend.",
      "FVGs at session open (London, NY) have high fill rates.",
    ],
  },
  {
    id: "wyckoff-accumulation",
    nombre: "Wyckoff — Accumulation Entry",
    tipo: "reversion",
    mercados: ["BTC", "ETH"],
    timeframes: ["4H", "D", "W"],
    dificultad: "avanzada",
    winRate: "50-60%",
    ratioRR: "1:5 — 1:10",
    descripcion: "Richard Wyckoff developed in the 1930s a method to understand how institutional operators accumulate and distribute positions. The accumulation phase is the period where large players buy silently before the big move up. Correctly identifying it offers the best R:R in the market.",
    condicionesEntrada: [
      "1. Preliminary Support (PS): first bounce after strong drop — first sign of buying interest.",
      "2. Selling Climax (SC): extreme bearish candle with very high volume — seller panic.",
      "3. Automatic Rally (AR): strong bounce from the SC — defines the range ceiling.",
      "4. Secondary Test (ST): returns to the SC level but with less volume — confirmation.",
      "5. Creek (resistance): price oscillates in range, forming highs and lows.",
      "6. Jump Across the Creek (JAC): clear breakout with volume from the range ceiling.",
      "7. Back Up to the Creek (BU): retest of the breakout — last entry opportunity.",
      "8. Entry on the BU with SL below the accumulation range.",
    ],
    stopLoss: "Daily candle close below the accumulation range (Selling Climax).",
    takeProfit: "Projection of the accumulation range height × 2-3 from the breakout.",
    ejemploReal: "BTC in 2020: accumulation range 6,000-12,000. SC in March 2020 (~4,000). AR to 10,000. Range 6K-12K for 6 months. JAC in October 2020. BU to 12,000. Entry 12,500, SL 9,000, TP target 30,000+. Actual R:R: 1:10+.",
    erroresComunes: [
      "Identifying accumulations where there is only distribution — confusing phases.",
      "Entering before the JAC — the range can last weeks or months.",
      "Not waiting for the BU — entering at the JAC with less R:R.",
    ],
    consejosPro: [
      "Volume is key in Wyckoff — without volume there is no confirmation.",
      "Longer accumulation ranges (3-6 months) produce the best moves.",
      "BTC on weekly chart shows Wyckoff very clearly in 2019-2020 and 2022-2023.",
      "Combine with on-chain: Wyckoff accumulation + Exchange Outflow = very powerful signal.",
    ],
  },
  {
    id: "breakout-retest",
    nombre: "Breakout + Resistance Retest",
    tipo: "ruptura",
    mercados: ["All"],
    timeframes: ["1H", "4H", "D"],
    dificultad: "basica",
    winRate: "55-65%",
    ratioRR: "1:2 — 1:4",
    descripcion: "One of the most classic and effective strategies. Price breaks a key resistance with volume, returns to test that level (now support) and offers a low-risk entry with the trend. The retest confirms that the old ceiling is the new floor.",
    condicionesEntrada: [
      "1. Identify a well-defined resistance (minimum 2-3 previous touches).",
      "2. Wait for breakout with CLEAR closing candle above the resistance.",
      "3. Confirm with above-average volume on the breakout candle.",
      "4. Wait for the retest — price returns to touch the broken line.",
      "5. On the retest, look for rejection candle (doji, hammer, engulfing) at the level.",
      "6. Entry at the close of the retest confirmation candle.",
    ],
    stopLoss: "Below the low of the retest confirmation candle (or below the broken resistance).",
    takeProfit: "Projection of the previous range height from the breakout level.",
    ejemploReal: "BTC resistance at 48,000 with 4 touches over 2 months. Breakout with daily candle closing at 49,500 with 2x volume. Retest to 48,200, hammer confirmation. Entry 48,500, SL 47,200, TP 54,000. R:R ≈ 1:4.",
    erroresComunes: [
      "Entering ON the breakout without waiting for the retest — high risk of false breakout.",
      "The retest doesn't always reach the exact level — allow ±1-2% margin.",
      "Breakouts without volume have high failure rates.",
    ],
    consejosPro: [
      "The ideal retest touches the level with lower volume than the breakout.",
      "If the retest drops far below the level, the setup is invalidated.",
      "Combine with EMA200 or an Order Block as additional confluence.",
      "In crypto, all-time high (ATH) breakouts are especially powerful.",
    ],
  },
  {
    id: "london-breakout",
    nombre: "London Open Breakout",
    tipo: "ruptura",
    mercados: ["BTC", "ETH"],
    timeframes: ["15m", "1H"],
    dificultad: "basica",
    winRate: "55-60%",
    ratioRR: "1:2 — 1:3",
    descripcion: "The London session (7:00-10:00 CET) is the most volatile in the crypto market along with the NY open. European institutions enter the market and create direct moves. Price typically breaks the range formed during the Asian night.",
    condicionesEntrada: [
      "1. Identify the Asian session range (00:00-07:00 CET): high and low.",
      "2. Mark those levels on the 15-minute chart.",
      "3. From 07:00 CET wait for the breakout of the Asian range.",
      "4. Enter at the close of the first 15m candle that clearly breaks the range.",
      "5. Alternative: wait for micro-retest of the broken range on 5 minutes.",
    ],
    stopLoss: "At the opposite extreme of the Asian range.",
    takeProfit: "Projection of the Asian range height from the breakout point.",
    ejemploReal: "BTC in range 43,500-44,200 during the night. At 7:15 CET a 15m candle closes at 44,350. Entry 44,350, SL 43,500, TP 44,900.",
    erroresComunes: [
      "Trading the London Breakout against the 4H trend.",
      "Days with important macroeconomic events: do not use this strategy.",
      "Very narrow Asian range (<0.5%) gives poor R:R.",
    ],
    consejosPro: [
      "Works best when the Asian range lasts all night without fakeouts.",
      "Combine with the day's macro bias: if the market is in uptrend, only upside breakouts.",
      "Mondays and Tuesdays have the cleanest London Breakouts statistically.",
    ],
  },
];

export const ESTRATEGIA_TIPOS = [
  { id: "todas", label: "All" },
  { id: "tendencia", label: "Trend" },
  { id: "reversion", label: "Reversal" },
  { id: "ruptura", label: "Breakout" },
  { id: "rango", label: "Range" },
  { id: "scalping", label: "Scalping" },
];

export const ESTRATEGIA_NIVELES = [
  { id: "todas", label: "All" },
  { id: "basica", label: "Basic" },
  { id: "intermedia", label: "Intermediate" },
  { id: "avanzada", label: "Advanced" },
];

export const TIPO_COLORS = {
  tendencia: "border-emerald-400/40 bg-emerald-400/[0.08] text-emerald-200",
  reversion: "border-cyan-400/40 bg-cyan-400/[0.08] text-cyan-200",
  ruptura: "border-orange-400/40 bg-orange-400/[0.08] text-orange-200",
  rango: "border-purple-400/40 bg-purple-400/[0.08] text-purple-200",
  scalping: "border-red-400/40 bg-red-400/[0.08] text-red-200",
};

export const NIVEL_COLORS = {
  basica: "border-emerald-400/40 bg-emerald-400/[0.08] text-emerald-200",
  intermedia: "border-amber-400/40 bg-amber-400/[0.08] text-amber-200",
  avanzada: "border-red-400/40 bg-red-400/[0.08] text-red-200",
};

export const findEstrategia = (id) => ESTRATEGIAS.find((e) => e.id === id);